<template>
  <div class="jp">
    <div class="jp__bar">
      <span class="jp__title"><feather-icon icon="SmartphoneIcon" size="14"/> {{ msg('livepreview') }}</span>
      <b-button variant="flat-secondary" size="sm" class="btn-icon" :href="previewUrl" target="_blank"
                v-b-tooltip.hover :title="msg('previewsitehint')">
        <feather-icon icon="ExternalLinkIcon" size="13"/>
      </b-button>
    </div>

    <!--
      The REAL guest website in an iframe (its /preview route), fed the journey
      being edited via postMessage. Same components, same styles, same themes —
      it cannot drift from what guests actually see.
    -->
    <div class="jp-phone">
      <div class="jp-phone__notch"></div>
      <iframe
          ref="frame"
          class="jp-phone__frame"
          :src="previewUrl"
          title="Journey preview"
      />
    </div>
  </div>
</template>

<script>
import {mapGetters} from 'vuex'
import {VBTooltip} from 'bootstrap-vue'
import {toGuestView} from '@/model/journey/journeyLogic'

/** Preview tokens must match journeyLogic.js and the guest defaults. */
const PREVIEW_TOKENS = {'{checkInTime}': '15:00', '{checkOutTime}': '11:00'};

/**
 * Live preview = the guest website itself. The old hand-built Vuexy imitation
 * could never be "exactly the same"; an iframe of the real app always is.
 */
export default {
  name: 'journeyPreview',
  directives: {'b-tooltip': VBTooltip},
  props: {
    journey: {type: Object, default: null},
    products: {type: Array, default: () => []},
    focusStepId: {type: String, default: null},
    context: {type: Object, default: null},
  },
  data() {
    return {
      postTimer: null,
    }
  },
  computed: {
    ...mapGetters('translator', ['msg']),
    previewUrl() {
      const base = (window.config && window.config.checkinUrl) || 'http://localhost:3000';
      return base.replace(/\/+$/, '') + '/preview';
    },
    productsById() {
      return this.offerProducts.reduce((map, product) => {
        map[product.id] = product;
        return map;
      }, {});
    },
    /** Builder features in the offer shape the guest site expects. */
    offerProducts() {
      return (this.products || []).map(feature => ({
        id: feature.id,
        name: feature.name,
        description: feature.description,
        icon: feature.icon,
        imageUri: feature.imageUri,
        // org-level features carry no price — it comes from the property at
        // reservation time, so the preview honestly shows none
        rate: feature.rate != null ? feature.rate : null,
        finalPrice: feature.rate != null ? feature.rate : null,
        currency: feature.currency || null,
        amountAllowed: feature.amountAllowed === true,
        dailyPayment: feature.dailyPayment === true,
      }));
    },
    guestView() {
      const view = toGuestView(this.journey, 'en', this.productsById);
      if (!view) return null;
      // resolve {checkInTime}-style tokens and stub the action-task state,
      // mirroring what the backend does for a real reservation
      return {
        ...view,
        steps: (view.steps || []).map(step => ({
          ...step,
          condition: this.resolveTokens(step.condition),
          questions: (step.questions || []).map(question => ({
            ...question,
            condition: this.resolveTokens(question.condition),
            task: this.isAction(question.type)
                ? {
                  type: question.type,
                  paymentType: question.paymentType || null,
                  completed: false,
                  blocking: question.blocking === true,
                }
                : null,
          })),
        })),
      };
    },
    sampleContext() {
      const iso = date => date.toISOString().slice(0, 10);
      return {
        contractId: 'preview',
        guestFirstname: 'Anna',
        roomName: 'Deluxe Room 204',
        checkInDate: iso(new Date(Date.now() + 7 * 86400000)),
        checkOutDate: iso(new Date(Date.now() + 10 * 86400000)),
        totalNights: 3,
        numberOfGuests: 2,
        hotelName: 'Hotel Astoria',
        currency: 'EUR',
        ...(this.context || {}),
      };
    },
    focusStepIndex() {
      if (!this.focusStepId || !this.guestView) return null;
      const index = (this.guestView.steps || []).findIndex(step => step.id === this.focusStepId);
      return index >= 0 ? index : null;
    },
  },
  watch: {
    journey: {deep: true, handler: 'schedulePost'},
    products: 'schedulePost',
    focusStepId: 'schedulePost',
  },
  mounted() {
    window.addEventListener('message', this.onMessage);
  },
  beforeDestroy() {
    window.removeEventListener('message', this.onMessage);
    if (this.postTimer) clearTimeout(this.postTimer);
  },
  methods: {
    isAction(type) {
      return ['GUEST_DETAILS', 'IDENTITY_VERIFICATION', 'PAYMENT', 'SIGNATURE'].includes(type);
    },
    resolveTokens(condition) {
      if (!condition || !condition.value) return condition;
      let value = String(condition.value);
      Object.keys(PREVIEW_TOKENS).forEach(token => {
        value = value.split(token).join(PREVIEW_TOKENS[token]);
      });
      return {...condition, value};
    },
    onMessage(event) {
      // the preview page announces it is listening — send the current state
      if (event.data && event.data.type === 'thc24-preview-ready') {
        this.post();
      }
    },
    schedulePost() {
      if (this.postTimer) clearTimeout(this.postTimer);
      this.postTimer = setTimeout(this.post, 250);
    },
    post() {
      const frame = this.$refs.frame;
      if (!frame || !frame.contentWindow || !this.guestView) return;
      frame.contentWindow.postMessage({
        type: 'thc24-preview',
        payload: {
          Journey: this.guestView,
          Context: this.sampleContext,
          Response: null,
          Products: this.offerProducts,
        },
        stepIndex: this.focusStepIndex,
      }, '*');
    },
  },
}
</script>

<style lang="scss" scoped>
.jp {
  position: sticky;
  top: 84px;

  &__bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 8px;
  }

  &__title {
    font-weight: 700;
    color: #5e5873;
    font-size: 13px;
  }

}

.jp-phone {
  position: relative;
  margin: 0 auto;
  /* height fills the column (capped), width follows phone-ish proportions —
     a touch wider than a real 19.5:9 handset so forms breathe */
  height: min(calc(100vh - 132px), 780px);
  aspect-ratio: 9 / 18;
  max-width: 100%;
  border-radius: 34px;
  border: 9px solid #10141c;
  background: #141926;
  box-shadow: 0 20px 50px -16px rgba(0, 0, 0, .4);
  overflow: hidden;

  &__notch {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 104px;
    height: 15px;
    background: #10141c;
    border-radius: 0 0 12px 12px;
    z-index: 2;
    pointer-events: none;
  }

  &__frame {
    width: 100%;
    height: 100%;
    border: 0;
    display: block;
    background: #141926;
  }
}

.dark-layout {
  .jp__title {
    color: #d0d2d6;
  }
}
</style>
